import { z } from 'zod'

/** PHP json_encode turns empty associative arrays into JSON lists; coerce to record. */
export const jsonRecordSchema = z.preprocess(
  (value) => (Array.isArray(value) ? {} : value),
  z.record(z.string(), z.unknown()),
)

export const tenantBootstrapSchema = z.object({
  ok: z.boolean().optional(),
  tenant: z.object({
    tenant: z.string(),
    role: z.enum(['patient', 'staff']),
    host: z.string(),
    branding: z.object({
      appName: z.string(),
      shortName: z.string(),
      logoSrc: z.string().nullable(),
      primaryColor: z.string(),
      basePaths: z.object({
        patient: z.string(),
        staff: z.string(),
      }),
    }),
    capabilities: z.record(z.string(), z.boolean()),
    integrations: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
    database: z.object({
      slug: z.string(),
      user: z.string(),
    }),
  }),
  connection: z.object({
    key: z.string(),
    doctrine: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  }),
  rls: z.record(z.string(), z.string()),
  auth: z.object({
    requestCodePath: z.string(),
    channelsPath: z.string(),
    verifyCodePath: z.string(),
    refreshPath: z.string(),
    verifyPath: z.string(),
    csrfPath: z.string(),
    logoutPath: z.string(),
  }),
})

export const authUserSchema = z.object({
  id: z.number(),
  phone: z.string(),
  display_name: z.string(),
  email: z.string().nullable(),
  branches: z.array(z.number()),
  permissions: z.array(z.string()),
})

export const authRequestCodeSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  channel: z.string().optional(),
  phone_masked: z.string().optional(),
  debug_code: z.string().nullable().optional(),
  can_resend: z.boolean().optional(),
  identities: z.array(authUserSchema).optional(),
})

export const authVerifyCodeSchema = z.object({
  success: z.boolean(),
  access_token: z.string(),
  refresh_token: z.string(),
  csrf_token: z.string(),
  expires_in: z.number(),
  channel: z.string().nullable().optional(),
  user: authUserSchema,
})

export const authVerifySchema = z.object({
  success: z.boolean(),
  valid: z.boolean(),
  user: authUserSchema,
})

export const authRefreshSchema = z.object({
  success: z.boolean(),
  access_token: z.string(),
  expires_in: z.number(),
  user: authUserSchema,
})

export const patientProfileSchema = z.object({
  success: z.boolean(),
  tenant: z.string().optional(),
  patient: z.object({
    id: z.number(),
    full_name: z.string(),
    last_name: z.string(),
    first_name: z.string(),
    middle_name: z.string(),
    phone: z.string(),
    email: z.string().nullable(),
    birthdate: z.string().nullable(),
    gender: z.string().nullable(),
    branch_name: z.string().optional(),
  }),
  statistics: z.object({
    total_appointments: z.number(),
    upcoming_appointments: z.number(),
    active_plans: z.number(),
    completed_payments: z.number().optional(),
  }),
})

export const patientAppointmentSchema = z.object({
  id: z.number(),
  date: z.string(),
  doctor_name: z.string(),
  service_name: z.string(),
  clinic_name: z.string(),
  status: z.string(),
})

export const patientPromotionSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string(),
  image_url: z.string().nullable().optional(),
  button_text: z.string(),
  button_url: z.string(),
  priority: z.number().optional(),
  segment_name: z.string().optional(),
})

export const communicationAttachActionSchema = z.object({
  channel: z.string(),
  label: z.string(),
  url: z.string(),
})

export const communicationChannelOptionSchema = z.object({
  channel: z.string(),
  display_name: z.string(),
  available: z.boolean(),
  selectable: z.boolean(),
  selected: z.boolean(),
  blocked_reason: z.string().optional(),
  source: z.string().optional(),
  status: z.string().optional(),
  attach_action: communicationAttachActionSchema.nullable().optional(),
})

export const patientDashboardSchema = z.object({
  success: z.boolean(),
  tenant: z.string().optional(),
  patient: z.object({
    id: z.number(),
    pcode: z.number(),
    full_name: z.string(),
    phone: z.string(),
    email: z.string().nullable(),
    birth_date: z.string().nullable(),
    medical_card_number: z.string().nullable(),
  }),
  latest_appointments: z.array(patientAppointmentSchema),
  upcoming_appointments: z.array(patientAppointmentSchema),
  promotions: z.array(patientPromotionSchema),
  statistics: z.object({
    completed_appointments: z.number(),
    upcoming_appointments: z.number(),
    active_plans: z.number(),
    unread_messages: z.number(),
    loyalty_enabled: z.boolean(),
    loyalty_available: z.number().nullable(),
  }),
  loyalty: z.object({
    enabled: z.boolean(),
    balance: z.object({
      total_available: z.number().nullable(),
      personal_balance: z.number().nullable(),
      family_balance: z.number().nullable(),
    }),
    transactions: z.array(
      z.object({
        created_at: z.string(),
        transaction_type: z.string(),
        amount: z.number(),
        description: z.string(),
      }),
    ),
    transactions_total: z.number().optional(),
    program_guide: z.record(z.string(), z.unknown()).nullable().optional(),
  }),
})

export const patientAppointmentsSchema = z.object({
  success: z.boolean(),
  patient: z.object({
    id: z.number(),
    full_name: z.string(),
  }),
  appointments: z.array(patientAppointmentSchema),
  total: z.number(),
})

export const financeOperationSchema = z.object({
  id: z.number().optional(),
  date: z.string(),
  description: z.string(),
  amount: z.number(),
  type: z.string(),
  appointment_info: z.string().optional(),
})

export const patientFinanceSchema = z.object({
  success: z.boolean(),
  statistics: z.object({
    total_paid: z.number(),
    total_unpaid: z.number(),
    balance: z.number(),
  }),
  operations: z.array(financeOperationSchema),
})

export const planServiceSchema = z.object({
  id: z.number().optional(),
  service_name: z.string(),
  tooth_code: z.string().optional(),
  quantity: z.number().optional(),
  unit_price: z.number().optional(),
  total_price: z.number().optional(),
  is_executed: z.boolean().optional(),
  is_executed_flag: z.boolean().optional(),
  execution_date: z.string().nullable().optional(),
})

export const treatmentPlanSchema = z.object({
  id: z.number().optional(),
  uid: z.string().optional(),
  title: z.string().optional(),
  patient_id: z.number().nullable().optional(),
  patient_name: z.string().optional(),
  doctor_name: z.string().optional(),
  status: z.string().optional(),
  plan_status: z.string().optional(),
  plan_date: z.string().optional(),
  created_at: z.string().optional(),
  total_amount: z.number().optional(),
  total_positions: z.number().optional(),
  items: z.array(planServiceSchema).optional(),
  services: z.array(planServiceSchema).optional(),
  has_share: z.boolean().optional(),
})

export const constructorPlanSchema = z.object({
  id: z.number(),
  title: z.string(),
  plan_date: z.string().optional(),
  created_at: z.string().optional(),
  public_secret: z.string().nullable().optional(),
  public_url: z.string().nullable().optional(),
  public_link_active: z.boolean().optional(),
  share_in_patient_lk: z.boolean().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  materials: z.array(
    z.object({
      id: z.number(),
      type: z.string(),
      title: z.string(),
      file_size_formatted: z.string().nullable().optional(),
      created_at: z.string().nullable().optional(),
    }),
  ),
  video_consultations: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      url: z.string(),
      platform: z.string().optional(),
      source: z.string().optional(),
      created_at: z.string().nullable().optional(),
    }),
  ),
})

export const publicPlanInstallmentPaymentSchema = z.object({
  id: z.number().nullable().optional(),
  stage_id: z.number().nullable().optional(),
  stage_title: z.string().nullable().optional(),
  title: z.string(),
  amount: z.number().nullable().optional(),
  due_date: z.string().nullable().optional(),
  sort_order: z.number().nullable().optional(),
  status: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
})

export const publicPlanInstallmentSchema = z.object({
  enabled: z.boolean(),
  apply_to: z.string().nullable().optional(),
  stage_id: z.number().nullable().optional(),
  months: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  rate: z.number().nullable().optional(),
  start_date: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  schedule: z.array(publicPlanInstallmentPaymentSchema).optional(),
})

export const publicPlanMediaSchema = z.object({
  id: z.number(),
  type: z.string(),
  original_name: z.string().optional(),
  display_name: z.string().optional(),
  stored_name: z.string().optional(),
  mime_type: z.string().nullable().optional(),
  file_size: z.number().nullable().optional(),
  file_size_formatted: z.string().nullable().optional(),
  thumbnail_path: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  sort_order: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  share_in_patient_lk: z.boolean().optional(),
})

export const publicPlanStageCashOfferSchema = z.object({
  amount: z.number().nullable().optional(),
  note: z.string().nullable().optional(),
  deadline: z.string().nullable().optional(),
  method: z.string().nullable().optional(),
})

export const publicPlanServiceSchema = z.object({
  id: z.number(),
  service_id: z.number().nullable().optional(),
  service_name: z.string(),
  tooth_number: z.string().nullable().optional(),
  quantity: z.number().nullable().optional(),
  unit_price: z.number().nullable().optional(),
  original_unit_price: z.number().nullable().optional(),
  discounted_unit_price: z.number().nullable().optional(),
  total_price: z.number().nullable().optional(),
  priority: z.string().nullable().optional(),
  sort_order: z.number().nullable().optional(),
  source_plan_uid: z.string().nullable().optional(),
  source_item_id: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  live_unit_price: z.number().nullable().optional(),
  live_discounted_price: z.number().nullable().optional(),
  live_total_price: z.number().nullable().optional(),
  live_price_diff: z.number().nullable().optional(),
})

export const publicPlanStageSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().nullable().optional(),
  status: z.string(),
  sort_order: z.number().nullable().optional(),
  total_amount: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  amount_with_discount: z.number().nullable().optional(),
  amount_savings: z.number().nullable().optional(),
  doctor_id: z.number().nullable().optional(),
  doctor_name: z.string().nullable().optional(),
  duration_value: z.number().nullable().optional(),
  duration_unit: z.string().nullable().optional(),
  comment: z.string().nullable().optional(),
  doctors: z.array(z.unknown()).optional(),
  cash_offer: publicPlanStageCashOfferSchema.nullable().optional(),
  services: z.array(publicPlanServiceSchema),
})

export const publicPlanShareSchema = z.object({
  public_token: z.string().nullable().optional(),
  public_url: z.string().nullable().optional(),
  is_active: z.boolean(),
  expires_at: z.string().nullable().optional(),
  hide_prices: z.boolean().optional(),
  allow_materials: z.boolean().optional(),
  view_count: z.number().nullable().optional(),
  print_count: z.number().nullable().optional(),
})

export const publicTreatmentPlanSchema = z.object({
  id: z.number(),
  title: z.string(),
  patient_id: z.number().nullable().optional(),
  patient_name: z.string().nullable().optional(),
  patient_birthdate: z.string().nullable().optional(),
  patient_card_number: z.string().nullable().optional(),
  doctor_id: z.number().nullable().optional(),
  doctor_name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  status: z.string(),
  total_amount: z.number().nullable().optional(),
  total_services: z.number().nullable().optional(),
  completed_services: z.number().nullable().optional(),
  progress: z.number().nullable().optional(),
  plan_date: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  hide_prices: z.boolean().optional(),
  offer_deadline: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  plan_cash_offer: publicPlanStageCashOfferSchema.nullable().optional(),
  public_secret: z.string().nullable().optional(),
  public_link_active: z.boolean().optional(),
  public_link_expires_at: z.string().nullable().optional(),
  share: publicPlanShareSchema.optional(),
  view_count: z.number().nullable().optional(),
  print_count: z.number().nullable().optional(),
  allow_materials: z.boolean().optional(),
  stages: z.array(publicPlanStageSchema),
  media: z.array(publicPlanMediaSchema).optional(),
  installment: publicPlanInstallmentSchema.nullable().optional(),
  pricing_summary: z.object({
    has_live_prices: z.boolean(),
    total_amount: z.number().nullable().optional(),
    total_amount_with_discount: z.number().nullable().optional(),
    total_savings: z.number().nullable().optional(),
    source: z.string().nullable().optional(),
  }).nullable().optional(),
  custom_fields: z.array(
    z.object({
      id: z.number().optional(),
      label: z.string(),
      type: z.string().optional(),
      value: z.string().nullable().optional(),
    }),
  ).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  payment: z.object({
    providers: z.object({ yookassa: z.boolean().optional(), yandex_pay: z.boolean().optional() }).optional(),
    yandex_pay_split: z.boolean().optional(),
    cash_special: z.boolean().optional(),
    partial: z.object({
      enabled: z.boolean().optional(),
      min_rub: z.number().nullable().optional(),
      max_rub: z.number().nullable().optional(),
      presets: z.array(z.enum(['10', '25', '30', '50', '75', '100'])).optional(),
    }).nullable().optional(),
  }).nullable().optional(),
})

export const staffTreatmentPlanResponseSchema = z.object({
  success: z.boolean(),
  data: publicTreatmentPlanSchema,
})

export const staffPlansResponseSchema = z.object({
  success: z.boolean(),
  plans: z.array(treatmentPlanSchema),
  total: z.number().optional(),
})

export const publicTreatmentPlanResponseSchema = z.object({
  success: z.boolean(),
  data: publicTreatmentPlanSchema,
})

export const publicTreatmentPlanErrorSchema = z.object({
  success: z.boolean(),
  status: z.string(),
  error: z.string(),
})

export const publicTreatmentPlanEventResponseSchema = z.object({
  success: z.boolean(),
  data: z.record(z.string(), z.unknown()).optional(),
  status: z.string().optional(),
  error: z.string().optional(),
})

export const staffDashboardResponseSchema = z.object({
  success: z.boolean(),
  staff: z.object({
    id: z.number(),
    display_name: z.string(),
    role_label: z.string(),
    branch_names: z.array(z.string()),
  }),
  summary: z.object({
    appointments_today: z.number(),
    pending_tasks: z.number(),
    active_public_links: z.number(),
    unread_threads: z.number(),
    plans_in_work: z.number().nullable().optional(),
    my_active_plans: z.number().nullable().optional(),
  }),
  focus_blocks: z.array(z.object({
    title: z.string(),
    value: z.string(),
    description: z.string(),
  })),
  sources: z.object({
    appointments: z.string(),
    communications: z.string(),
    plans: z.string(),
    public_links: z.string(),
  }).optional(),
  today_appointments: z.array(z.object({
    id: z.number(),
    date: z.string(),
    patient_name: z.string(),
    service_name: z.string(),
    branch_name: z.string(),
    room_name: z.string(),
    status: z.string(),
  })),
})

export const staffPatientSegmentsInsightResponseSchema = z.object({
  success: z.boolean(),
  status: z.string().optional(),
  summary: z.string().optional(),
  segments_guidance: z.string().nullable().optional(),
  segments_count: z.number().optional(),
  warning: z.string().nullable().optional(),
  segments: z.array(z.object({
    segment_id: z.string().nullable().optional(),
    name: z.string(),
    description: z.string().nullable().optional(),
    guidance_text: z.string().nullable().optional(),
    has_guidance: z.boolean().optional(),
    is_favorite: z.boolean().optional(),
    for_mis: z.boolean().optional(),
  })).optional(),
})

export const staffPatientContextInsightResponseSchema = z.object({
  success: z.boolean(),
  status: z.string().optional(),
  summary: z.string().optional(),
  summary_preview: z.string().nullable().optional(),
  meta_summary_text: z.string().nullable().optional(),
})

export const staffScheduleResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    appointments: z.string(),
  }).optional(),
  days: z.array(z.object({
    date: z.string(),
    label: z.string(),
    appointments: z.number(),
  })),
  appointments: z.array(z.object({
    id: z.number(),
    patient_id: z.number().optional(),
    date: z.string(),
    start_time: z.string(),
    end_time: z.string(),
    doctor_name: z.string().optional(),
    patient_name: z.string(),
    service_name: z.string(),
    branch_name: z.string(),
    room_name: z.string(),
    status: z.string(),
    channel: z.string().optional(),
    note: z.string().optional(),
    visit_kind: z.enum(['primary', 'repeat']).optional(),
    visit_label: z.string().optional(),
    appointment_status_label: z.string().optional(),
    appointment_status_code: z.number().optional(),
    appointment_state: z.string().optional(),
    appointment_comment: z.string().optional(),
    card_comment: z.string().optional(),
  })),
})

export const staffPatientsListResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    patients: z.string(),
  }).optional(),
  summary: z.object({
    total: z.number(),
    loaded: z.number(),
    with_phone: z.number(),
    debtors: z.number(),
  }),
  filters: z.object({
    page: z.number(),
    per_page: z.number(),
    search: z.string(),
  }),
  total: z.number(),
  pages: z.number(),
  patients: z.array(z.object({
    id: z.number(),
    full_name: z.string(),
    phone: z.string().nullable().optional(),
    phone_hidden: z.boolean().optional(),
    birth_date: z.string().nullable().optional(),
    gender: z.string().nullable().optional(),
    last_visit_date: z.string().nullable().optional(),
    last_doctor_name: z.string().nullable().optional(),
    debt_amount: z.number().nullable().optional(),
    branch_name: z.string().optional(),
  })),
})

export const staffPatientMedicalCardsResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    medical_cards: z.string(),
  }).optional(),
  filters: z.object({
    from_date: z.string(),
    to_date: z.string(),
    doctor_id: z.number().nullable(),
    patient_id: z.number().nullable(),
    record_id: z.number().nullable(),
    limit: z.number(),
  }),
  summary: z.object({
    records: z.number(),
    patients: z.number(),
    doctors: z.number(),
  }),
  doctors: z.array(z.object({
    doctor_id: z.number().nullable(),
    doctor_name: z.string(),
    record_count: z.number(),
    share_percent: z.number(),
  })),
  records: z.array(z.object({
    record_id: z.number(),
    patient_id: z.number(),
    doctor_id: z.number().nullable().optional(),
    patient_name: z.string(),
    doctor_name: z.string(),
    record_date: z.string(),
    record_type: z.string(),
    summary: z.string(),
  })),
  selected_patient: z.object({
    patient_id: z.number(),
    patient_name: z.string(),
    records: z.array(z.object({
      record_id: z.number(),
      patient_id: z.number(),
      doctor_id: z.number().nullable().optional(),
      patient_name: z.string(),
      doctor_name: z.string(),
      record_date: z.string(),
      record_type: z.string(),
      summary: z.string(),
    })),
  }).nullable().optional(),
  selected_record: z.object({
    record_id: z.number(),
    patient_id: z.number(),
    doctor_id: z.number().nullable().optional(),
    patient_name: z.string(),
    doctor_name: z.string(),
    record_date: z.string(),
    record_type: z.string(),
    summary: z.string(),
    comment: z.string().nullable().optional(),
    params: z.array(z.object({
      key: z.string(),
      label: z.string(),
      value: z.string(),
    })),
  }).nullable().optional(),
})

export const staffPatientCommentsResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    comments: z.string(),
  }).optional(),
  summary: z.object({
    total: z.number(),
    with_comment: z.number(),
    without_comment: z.number(),
  }),
  comments: z.array(z.object({
    id: z.number(),
    patient_id: z.number(),
    patient_name: z.string(),
    doctor_name: z.string(),
    date: z.string(),
    service_name: z.string(),
    comment: z.string(),
    status: z.string(),
    has_comment: z.boolean(),
  })),
})

export const staffPatientSegmentsResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    segments: z.string(),
  }).optional(),
  summary: z.object({
    total_segments: z.number(),
    lk_segments: z.number(),
    dynamic_segments: z.number(),
  }),
  segments: z.array(z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullable().optional(),
    type: z.string(),
    patients_count: z.number(),
    for_lk: z.boolean(),
  })),
})

export const staffPatientVerificationCodesResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    verification_codes: z.string(),
  }).optional(),
  summary: z.object({
    total: z.number(),
    active: z.number(),
    expired: z.number(),
  }),
  codes: z.array(z.object({
    phone: z.string(),
    phone_hidden: z.boolean().optional(),
    pcode: z.number().nullable().optional(),
    code: z.string(),
    created_at: z.string(),
    expires_at: z.string(),
    is_expired: z.boolean(),
  })),
})

export const staffPatientActivityResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    activity: z.string(),
  }).optional(),
  filters: z.object({
    days: z.number(),
    search: z.string(),
    event_type: z.string().nullable().optional(),
    limit: z.number(),
    exclude_verification_script: z.boolean().optional().default(false),
  }),
  summary: z.object({
    total: z.number(),
    logins: z.number(),
    auth_failures: z.number().optional().default(0),
    navigations: z.number(),
    business: z.number().optional().default(0),
    errors: z.number(),
    unique_patients: z.number(),
  }),
  events: z.array(z.object({
    id: z.number(),
    patient_id: z.number().nullable().optional(),
    patient_pcode: z.number().nullable().optional(),
    patient_name: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    phone_hidden: z.boolean().optional(),
    event_type: z.string(),
    title: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    view: z.string().nullable().optional(),
    view_label: z.string().nullable().optional(),
    path: z.string().nullable().optional(),
    created_at: z.string(),
    payload: z.record(z.string(), z.unknown()).optional(),
  })),
  errors: z.array(z.object({
    id: z.number(),
    patient_id: z.number().nullable().optional(),
    patient_pcode: z.number().nullable().optional(),
    patient_name: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    phone_hidden: z.boolean().optional(),
    event_type: z.string(),
    title: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    view: z.string().nullable().optional(),
    view_label: z.string().nullable().optional(),
    path: z.string().nullable().optional(),
    created_at: z.string(),
    payload: z.record(z.string(), z.unknown()).optional(),
  })).optional(),
})

export const staffCommunicationsResponseSchema = z.object({
  success: z.boolean(),
  summary: z.object({
    unread_threads: z.number(),
    patients_waiting_reply: z.number(),
    preferred_whatsapp: z.number(),
    manual_follow_up: z.number(),
  }),
  channel_overview: z.array(z.object({
    channel: z.string(),
    label: z.string(),
    count: z.number(),
  })),
  threads: z.array(z.object({
    id: z.number(),
    patient_name: z.string(),
    channel: z.string(),
    subject: z.string(),
    last_message: z.string(),
    last_message_at: z.string(),
    unread_count: z.number(),
    status: z.string(),
    owner_name: z.string(),
    preferred_channel: z.string(),
  })),
})

export const staffUsersUserSchema = z.object({
  id: z.number(),
  phone: z.string(),
  display_name: z.string(),
  full_name: z.string().nullable().optional(),
  email: z.string().nullable(),
  branches: z.array(z.number()),
  permissions: z.array(z.string()),
  role_key: z.string(),
  role_label: z.string(),
  is_current_user: z.boolean(),
  is_active: z.boolean().optional(),
  linked_doctor_id: z.number().nullable().optional(),
  branch_labels: z.array(z.string()),
})

export const staffUsersResponseSchema = z.object({
  success: z.boolean(),
  summary: z.object({
    staff_total: z.number(),
    roles_total: z.number(),
    branches_total: z.number(),
  }),
  users: z.array(staffUsersUserSchema),
  permission_matrix: z.array(z.object({
    permission: z.string(),
    label: z.string(),
    holders: z.array(z.string()),
    total: z.number(),
  })),
})

export const staffUsersCatalogResponseSchema = z.object({
  success: z.boolean(),
  permissions: z.array(z.object({ key: z.string(), label: z.string() })),
  roles: z.array(z.object({
    key: z.string(),
    label: z.string(),
    permissions: z.array(z.string()),
  })),
})

export const staffUsersWriteUserSchema = z.object({
  id: z.number(),
  phone: z.string(),
  display_name: z.string(),
  full_name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  role_key: z.string(),
  branch_ids: z.array(z.number()),
  permissions: z.array(z.string()),
  linked_doctor_id: z.number().nullable().optional(),
  is_active: z.boolean(),
})

export const staffUsersMutationResponseSchema = z.object({
  success: z.boolean(),
  user: staffUsersWriteUserSchema,
})

export const staffOperationsMetricSchema = z.object({
  label: z.string(),
  value: z.string(),
})

export const staffOperationsItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  value: z.string().optional(),
  status: z.string().optional(),
})

export const staffOperationsPanelSchema = z.object({
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  description: z.string(),
  metrics: z.array(staffOperationsMetricSchema),
  items: z.array(staffOperationsItemSchema),
})

export const staffOperationsResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    operations: z.string(),
  }),
  panels: z.array(staffOperationsPanelSchema),
})

export const staffGiftCertificateThemeSchema = z.object({
  primary: z.string().optional(),
  backgroundImageUrl: z.string().nullable().optional(),
})

export const staffGiftCertificateShareSchema = z.object({
  token: z.string().nullable().optional(),
  public_url: z.string().nullable().optional(),
  public_api_url: z.string().nullable().optional(),
  view_count: z.number().optional(),
})

export const staffGiftCertificateTemplateSchema = z.object({
  id: z.string(),
  title: z.string(),
  brand_name: z.string(),
  offer_text: z.string(),
  support_text: z.string(),
  theme: staffGiftCertificateThemeSchema,
})

export const staffGiftCertificateSchema = z.object({
  id: z.string(),
  patient_id: z.number().nullable().optional(),
  template_id: z.string(),
  template_title: z.string(),
  recipient_name: z.string(),
  brand_name: z.string(),
  offer_text: z.string(),
  support_text: z.string(),
  amount_value: z.number(),
  amount_unit: z.string(),
  amount_label: z.string(),
  issued_at: z.string(),
  expires_at: z.string(),
  status: z.string(),
  theme: staffGiftCertificateThemeSchema,
  share: staffGiftCertificateShareSchema.optional(),
})

export const staffGiftCertificatesResponseSchema = z.object({
  success: z.boolean(),
  summary: z.object({
    templates_total: z.number(),
    issued_total: z.number(),
    active_total: z.number(),
  }),
  templates: z.array(staffGiftCertificateTemplateSchema),
  issued: z.array(staffGiftCertificateSchema),
})

export const staffGiftCertificateMutationResponseSchema = z.object({
  success: z.boolean(),
  data: staffGiftCertificateSchema,
})

export const patientPlansSchema = z.object({
  success: z.boolean(),
  plans: z.array(treatmentPlanSchema),
  total: z.number().optional(),
  filtered: z.boolean().optional(),
  show_all: z.boolean().optional(),
})

export const patientConstructorPlansSchema = z.object({
  success: z.boolean(),
  plans: z.array(constructorPlanSchema),
})

export const usefulFormFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'tel', 'textarea', 'select', 'number', 'email']),
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  options: z.array(z.string()).optional(),
})

export const usefulContentItemSchema = z.object({
  id: z.number().optional(),
  title: z.string().optional(),
  summary: z.string().optional(),
  preview_text: z.string().optional(),
  body: z.string().optional(),
  fields: z.array(usefulFormFieldSchema).optional(),
})

export const usefulSectionSchema = z.object({
  slug: z.string(),
  title: z.string(),
  summary: z.string().optional().default(''),
  body: z.string().optional().default(''),
  body_html: z.string().optional(),
  cta_label: z.string().optional(),
  cta_url: z.string().optional(),
  button_text: z.string().optional(),
  button_url: z.string().optional(),
  kind: z.string().optional(),
  live: z.boolean().optional(),
  form: z.array(usefulFormFieldSchema).optional(),
  items: z.array(usefulContentItemSchema).optional(),
})

export const patientUsefulSchema = z.object({
  success: z.boolean(),
  sections: z.array(usefulSectionSchema),
})

export const reviewRewardFileSchema = z.object({
  id: z.number(),
  originalName: z.string(),
  url: z.string(),
  mimeType: z.string().optional(),
})

export const reviewRewardPreviewSchema = z.object({
  brushExpected: z.boolean(),
  brushName: z.string(),
  bonusAmount: z.number(),
  summary: z.string(),
})

export const reviewRewardRequestSchema = z.object({
  id: z.number(),
  platforms: z.array(z.string()),
  platformLabels: z.array(z.string()),
  rewardPreview: reviewRewardPreviewSchema,
  note: z.string().nullable().optional(),
  status: z.enum(['pending', 'approved', 'rejected']),
  moderatorNote: z.string().nullable().optional(),
  files: z.array(reviewRewardFileSchema),
  createdAt: z.string(),
})

export const patientReviewsSchema = z.object({
  success: z.boolean(),
  items: z.array(reviewRewardRequestSchema),
  catalog: z.record(z.string(), z.object({ label: z.string() })),
})

export const patientPortalItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  body: z.string().optional(),
  value: z.string().optional(),
  url: z.string().optional(),
  status: z.string().optional(),
})

export const patientPortalPageSchema = z.object({
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  body: z.string(),
  group: z.string(),
  kind: z.string(),
  badge: z.string().optional(),
  button_text: z.string().optional(),
  button_url: z.string().optional(),
  items: z.array(patientPortalItemSchema),
})

export const patientPortalSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    portal: z.string(),
  }),
  pages: z.array(patientPortalPageSchema),
})

export const patientThreadPinSchema = z.object({
  id: z.number(),
  message_id: z.number(),
  title: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  badge_label: z.string().nullable().optional(),
  created_at: z.string(),
})

export const patientThreadMessageSchema = z.object({
  id: z.number(),
  sender_kind: z.string(),
  sender_name: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  created_at: z.string(),
  attachments: z.array(z.record(z.string(), z.unknown())).optional(),
  metadata: jsonRecordSchema.nullable().optional(),
  pin: patientThreadPinSchema.nullable().optional(),
})

export const patientThreadSchema = z.object({
  success: z.boolean(),
  data: z.object({
    thread: z.object({
      id: z.number(),
      threadKey: z.string(),
      title: z.string(),
      replyEnabled: z.boolean(),
      status: z.string(),
      unreadCount: z.number(),
      typingActor: z.enum(['ai', 'operator']).nullable().optional(),
    }),
    messages: z.array(patientThreadMessageSchema),
    pins: z.array(patientThreadPinSchema),
  }),
})

export const giftCertificateSchema = z.object({
  success: z.boolean(),
  data: z.object({
    title: z.string(),
    recipientName: z.string(),
    offerText: z.string(),
    supportText: z.string(),
    brandName: z.string(),
    amountLabel: z.string().optional(),
    status: z.string().optional(),
    expiresAt: z.string().optional(),
    theme: z.object({
      primary: z.string(),
      backgroundImageUrl: z.string().nullable(),
    }),
  }),
})

export const publicGiftCertificateSchema = z.object({
  success: z.boolean(),
  data: z.object({
    title: z.string(),
    recipient_name: z.string(),
    brand_name: z.string(),
    offer_text: z.string(),
    support_text: z.string(),
    amount_value: z.number(),
    amount_unit: z.string(),
    amount_label: z.string(),
    status: z.string(),
    expires_at: z.string(),
    issued_at: z.string(),
    view_count: z.number(),
    theme: staffGiftCertificateThemeSchema,
  }),
})

export const communicationPreferencesSchema = z.object({
  patient: z.object({
    id: z.string(),
    external_patient_id: z.string().nullable().optional(),
    phone: z.string().optional(),
    phone_normalized: z.string(),
    full_name: z.string().optional(),
    current_preferred_channel: z.string().nullable().optional(),
    current_preferred_channel_source_code: z.string().nullable().optional(),
    current_preferred_channel_source_label: z.string().nullable().optional(),
    current_preferred_channel_set_at: z.string().nullable().optional(),
  }),
  channel_options: z.array(communicationChannelOptionSchema),
  attach_actions: z.array(communicationAttachActionSchema).optional(),
  employee_found: z.boolean().optional(),
  employee_display_name: z.string().optional(),
})

export const communicationPreferencesUpdateSchema = z.object({
  success: z.boolean(),
  result: communicationPreferencesSchema,
})

export const staffPatientWorkspaceActionSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  status: z.string(),
})

export const staffPatientWorkspaceTimelineItemSchema = z.object({
  id: z.number(),
  date: z.string(),
  title: z.string(),
  status: z.string(),
  doctor_name: z.string().optional(),
  description: z.string().optional(),
  value: z.string().optional(),
})


export const staffPatientDiagnocatAnalysisSchema = z.object({
  analysis_uid: z.string(),
  title: z.string(),
  analysis_type: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  has_pdf: z.boolean(),
})

export const staffPatientDiagnocatStudySchema = z.object({
  study_uid: z.string(),
  study_name: z.string().nullable().optional(),
  study_type: z.string().nullable().optional(),
  study_date: z.string().nullable().optional(),
  analyses: z.array(staffPatientDiagnocatAnalysisSchema),
})

export const staffPatientDiagnocatMatchSchema = z.object({
  candidate_id: z.string(),
  candidate_label: z.string().nullable().optional(),
  score: z.number().optional(),
  birth_date_match: z.boolean().nullable().optional(),
})

export const staffPatientDiagnocatResponseSchema = z.object({
  success: z.literal(true),
  sources: z.record(z.string(), z.string()),
  linked: z.boolean(),
  status: z.string(),
  legacy_patient_id: z.number().optional(),
  total_studies: z.number(),
  total_analyses: z.number(),
  ambiguous_matches: z.array(staffPatientDiagnocatMatchSchema).optional().default([]),
  studies: z.array(staffPatientDiagnocatStudySchema),
})

export const staffPatientDiagnocatPdfResponseSchema = z.object({
  success: z.literal(true),
  pdf_url: z.string(),
})

export type StaffPatientDiagnocatResponse = z.infer<typeof staffPatientDiagnocatResponseSchema>

export const staffPatientWorkspaceResponseSchema = z.object({
  success: z.boolean(),
  sources: z.object({
    workspace: z.string(),
  }),
  patient: z.object({
    id: z.number(),
    full_name: z.string(),
    phone: z.string().nullable().optional(),
    phone_hidden: z.boolean().optional(),
    birth_date: z.string().optional(),
    branch_name: z.string().optional(),
    last_doctor_name: z.string().optional(),
    tags: z.array(z.string()).optional(),
  }),
  summary: z.object({
    upcoming_appointments: z.number(),
    completed_appointments: z.number(),
    active_plans: z.number(),
    outstanding_balance: z.number(),
    unread_threads: z.number(),
  }),
  appointments: z.array(staffPatientWorkspaceTimelineItemSchema),
  financial_records: z.array(staffPatientWorkspaceTimelineItemSchema),
  treatment_history: z.array(staffPatientWorkspaceTimelineItemSchema),
  actions: z.array(staffPatientWorkspaceActionSchema),
})

export const patientBookingDoctorSchema = z.object({
  doctor_code: z.string(),
  full_name: z.string(),
  specialty_name: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  login: z.string().nullable().optional(),
  photo_url: z.string().nullable().optional(),
  photo_alt: z.string().nullable().optional(),
})

export const patientBookingDateSchema = z.object({
  date: z.string(),
  available_count: z.number(),
  load_level: z.string().nullable().optional(),
  is_full: z.boolean(),
})

export const patientBookingTimeSchema = z.object({
  start_time: z.string(),
  end_time: z.string(),
  schedident: z.string().nullable().optional(),
})

export const patientBookingDoctorsSchema = z.object({
  success: z.boolean(),
  doctors: z.array(patientBookingDoctorSchema),
})

export const patientBookingDatesSchema = z.object({
  success: z.boolean(),
  dates: z.array(patientBookingDateSchema),
})

export const patientBookingTimesSchema = z.object({
  success: z.boolean(),
  times: z.array(patientBookingTimeSchema),
})

export const patientBookingOtpSchema = z.object({
  success: z.boolean(),
  otp_required: z.boolean().optional(),
  phone_masked: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  status_html: z.string().nullable().optional(),
})

export const patientDhcBookingServiceSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  price: z.number().nullable().optional(),
})

export const patientDhcBookingStaffSchema = z.object({
  id: z.string(),
  name: z.string(),
  specialization: z.object({
    id: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
  }).nullable().optional(),
  experience: z.string().nullable().optional(),
  photo: z.string().nullable().optional(),
  rating: z.string().nullable().optional(),
})

export const patientDhcBookingDateSchema = z.object({
  date: z.string(),
  datetime: z.string(),
  available: z.boolean().optional(),
})

export const patientDhcBookingTimeSchema = z.object({
  time: z.string(),
  datetime: z.string(),
  duration: z.number().nullable().optional(),
  available: z.boolean().optional(),
})

export const patientDhcBookingServicesSchema = z.object({
  success: z.boolean(),
  services: z.array(patientDhcBookingServiceSchema),
})

export const patientDhcBookingStaffResponseSchema = z.object({
  success: z.boolean(),
  staff: z.array(patientDhcBookingStaffSchema),
})

export const patientDhcBookingDatesSchema = z.object({
  success: z.boolean(),
  dates: z.array(patientDhcBookingDateSchema),
})

export const patientDhcBookingTimesSchema = z.object({
  success: z.boolean(),
  times: z.array(patientDhcBookingTimeSchema),
})

export const patientDhcBookingSubmitSchema = z.object({
  success: z.boolean(),
  data: z.object({
    booking_id: z.string().nullable().optional(),
    message: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
  }),
})

export const authChannelsResponseSchema = z.object({
  success: z.boolean(),
  channels: z.array(z.string()),
  channel_options: z.array(communicationChannelOptionSchema).optional(),
  attach_actions: z.array(communicationAttachActionSchema).optional(),
  employee_found: z.boolean().optional(),
  employee_display_name: z.string().optional(),
})

export type TenantBootstrapResponse = z.infer<typeof tenantBootstrapSchema>
export type AuthUser = z.infer<typeof authUserSchema>
export type AuthRequestCodeResponse = z.infer<typeof authRequestCodeSchema>
export type AuthChannelsResponse = z.infer<typeof authChannelsResponseSchema>
export type AuthVerifyCodeResponse = z.infer<typeof authVerifyCodeSchema>
export type AuthVerifyResponse = z.infer<typeof authVerifySchema>
export type AuthRefreshResponse = z.infer<typeof authRefreshSchema>
export type PatientProfileResponse = z.infer<typeof patientProfileSchema>
export type PatientDashboardResponse = z.infer<typeof patientDashboardSchema>
export type PatientAppointmentsResponse = z.infer<typeof patientAppointmentsSchema>
export type CommunicationPreferencesResponse = z.infer<typeof communicationPreferencesSchema>
export type CommunicationPreferencesUpdateResponse = z.infer<typeof communicationPreferencesUpdateSchema>
export type PatientBookingDoctorsResponse = z.infer<typeof patientBookingDoctorsSchema>
export type PatientBookingDatesResponse = z.infer<typeof patientBookingDatesSchema>
export type PatientBookingTimesResponse = z.infer<typeof patientBookingTimesSchema>
export type PatientBookingOtpResponse = z.infer<typeof patientBookingOtpSchema>
export type PatientDhcBookingServicesResponse = z.infer<typeof patientDhcBookingServicesSchema>
export type PatientDhcBookingStaffResponse = z.infer<typeof patientDhcBookingStaffResponseSchema>
export type PatientDhcBookingDatesResponse = z.infer<typeof patientDhcBookingDatesSchema>
export type PatientDhcBookingTimesResponse = z.infer<typeof patientDhcBookingTimesSchema>
export type PatientDhcBookingSubmitResponse = z.infer<typeof patientDhcBookingSubmitSchema>
export type PatientFinanceResponse = z.infer<typeof patientFinanceSchema>
export type TreatmentPlan = z.infer<typeof treatmentPlanSchema>
export type ConstructorPlan = z.infer<typeof constructorPlanSchema>
export type PatientPlansResponse = z.infer<typeof patientPlansSchema>
export type PatientConstructorPlansResponse = z.infer<typeof patientConstructorPlansSchema>
export type PublicPlanInstallment = z.infer<typeof publicPlanInstallmentSchema>
export type PublicPlanInstallmentPayment = z.infer<typeof publicPlanInstallmentPaymentSchema>
export type PublicPlanMedia = z.infer<typeof publicPlanMediaSchema>
export type PublicPlanService = z.infer<typeof publicPlanServiceSchema>
export type PublicPlanStage = z.infer<typeof publicPlanStageSchema>
export type PublicPlanShare = z.infer<typeof publicPlanShareSchema>
export type PublicTreatmentPlan = z.infer<typeof publicTreatmentPlanSchema>
export type StaffPlansResponse = z.infer<typeof staffPlansResponseSchema>
export type StaffTreatmentPlanResponse = z.infer<typeof staffTreatmentPlanResponseSchema>
export type PublicTreatmentPlanResponse = z.infer<typeof publicTreatmentPlanResponseSchema>
export type PublicTreatmentPlanErrorResponse = z.infer<typeof publicTreatmentPlanErrorSchema>
export type PublicTreatmentPlanEventResponse = z.infer<typeof publicTreatmentPlanEventResponseSchema>
export type StaffDashboardResponse = z.infer<typeof staffDashboardResponseSchema>
export type StaffScheduleResponse = z.infer<typeof staffScheduleResponseSchema>
export type StaffPatientsListResponse = z.infer<typeof staffPatientsListResponseSchema>
export type StaffPatientMedicalCardsResponse = z.infer<typeof staffPatientMedicalCardsResponseSchema>
export type StaffPatientCommentsResponse = z.infer<typeof staffPatientCommentsResponseSchema>
export type StaffPatientSegmentsResponse = z.infer<typeof staffPatientSegmentsResponseSchema>
export type StaffPatientVerificationCodesResponse = z.infer<typeof staffPatientVerificationCodesResponseSchema>
export type StaffPatientActivityResponse = z.infer<typeof staffPatientActivityResponseSchema>
export type StaffPatientWorkspaceResponse = z.infer<typeof staffPatientWorkspaceResponseSchema>
export type StaffCommunicationsResponse = z.infer<typeof staffCommunicationsResponseSchema>
export type StaffOperationsResponse = z.infer<typeof staffOperationsResponseSchema>
export type StaffUsersResponse = z.infer<typeof staffUsersResponseSchema>
export type StaffUsersUser = z.infer<typeof staffUsersUserSchema>
export type StaffUsersCatalogResponse = z.infer<typeof staffUsersCatalogResponseSchema>
export type StaffUsersWriteUser = z.infer<typeof staffUsersWriteUserSchema>
export type StaffUsersMutationResponse = z.infer<typeof staffUsersMutationResponseSchema>
export type StaffGiftCertificateTemplate = z.infer<typeof staffGiftCertificateTemplateSchema>
export type StaffGiftCertificate = z.infer<typeof staffGiftCertificateSchema>
export type StaffGiftCertificatesResponse = z.infer<typeof staffGiftCertificatesResponseSchema>
export type StaffGiftCertificateMutationResponse = z.infer<typeof staffGiftCertificateMutationResponseSchema>
export type PatientUsefulResponse = z.infer<typeof patientUsefulSchema>
export type PatientPortalResponse = z.infer<typeof patientPortalSchema>
export type PatientThreadResponse = z.infer<typeof patientThreadSchema>
export type GiftCertificateResponse = z.infer<typeof giftCertificateSchema>
export type PublicGiftCertificateResponse = z.infer<typeof publicGiftCertificateSchema>

// ── Staff Promotions (Segments API) ──────────────────────────────────────────

export const staffSegmentSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  title: z.string().optional(),
  type: z.enum(['static', 'dynamic']).optional(),
  patients_count: z.number().optional(),
  for_lk: z.boolean().optional(),
})
export type StaffSegment = z.infer<typeof staffSegmentSchema>

export const staffSegmentsResponseSchema = z.object({
  success: z.boolean(),
  segments: z.array(staffSegmentSchema),
})
export type StaffSegmentsResponse = z.infer<typeof staffSegmentsResponseSchema>

export const staffPromotionSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().optional().default(''),
  content: z.string().optional().default(''),
  image_url: z.string().optional().nullable(),
  button_text: z.string().optional().default('Подробнее'),
  button_url: z.string().optional().default(''),
  start_date: z.string().optional().nullable(),
  end_date: z.string().optional().nullable(),
  priority: z.number().optional().default(0),
  is_active: z.boolean().optional().default(true),
  /** segment_ids or segments array (normalized server-side) */
  segment_ids: z.array(z.number()).optional().default([]),
  segments: z.array(z.object({
    id: z.number(),
    name: z.string().optional(),
    type: z.string().optional(),
  })).optional(),
})
export type StaffPromotion = z.infer<typeof staffPromotionSchema>

export const staffPromotionsListResponseSchema = z.object({
  success: z.boolean(),
  promotions: z.array(staffPromotionSchema),
})
export type StaffPromotionsListResponse = z.infer<typeof staffPromotionsListResponseSchema>

export const staffPromotionMutationResponseSchema = z.object({
  success: z.boolean(),
  promotion: staffPromotionSchema.optional(),
})
export type StaffPromotionMutationResponse = z.infer<typeof staffPromotionMutationResponseSchema>

export type StaffPromotionInput = {
  title: string
  description: string
  content: string
  image_url: string
  button_text: string
  button_url: string
  start_date: string
  end_date: string
  priority: number
  is_active: boolean
  segment_ids: number[]
}

// ─────────────────────── Payment Links ──────────────────────────────────────

export const paymentProviderSchema = z.enum(['yookassa', 'yandex_pay'])
export type PaymentProvider = z.infer<typeof paymentProviderSchema>

export const paymentStatusSchema = z.enum([
  'pending',
  'waiting_for_capture',
  'paid',
  'canceled',
  'refunded',
  'expired',
])
export type PaymentStatus = z.infer<typeof paymentStatusSchema>

export const paymentPartialConfigSchema = z.object({
  enabled: z.boolean(),
  min_rub: z.number().nullable().optional(),
  max_rub: z.number().nullable().optional(),
  presets: z.array(z.enum(['10', '25', '30', '50', '75', '100'])).optional(),
})
export type PaymentPartialConfig = z.infer<typeof paymentPartialConfigSchema>

export const paymentSettingsSchema = z.object({
  providers: z.object({
    yookassa: z.boolean().optional(),
    yandex_pay: z.boolean().optional(),
  }).optional(),
  yandex_pay_split: z.boolean().optional(),
  cash_special: z.boolean().optional(),
  partial: paymentPartialConfigSchema.optional(),
})
export type PaymentSettings = z.infer<typeof paymentSettingsSchema>

export const paymentLinkSchema = z.object({
  id: z.number(),
  entity_type: z.string(),
  entity_id: z.number().nullable().optional(),
  provider: paymentProviderSchema,
  amount_kopecks: z.number(),
  amount_rub: z.number(),
  currency: z.string(),
  status: paymentStatusSchema,
  yandex_split: z.boolean(),
  payment_url: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  patient_phone: z.string().nullable().optional(),
  patient_label: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  paid_at: z.string().nullable().optional(),
  provider_payment_id: z.string().nullable().optional(),
})
export type PaymentLink = z.infer<typeof paymentLinkSchema>

export const createPaymentResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    id: z.number(),
    payment_url: z.string(),
    provider: paymentProviderSchema,
    status: paymentStatusSchema,
    amount_kopecks: z.number(),
  }),
})
export type CreatePaymentResponse = z.infer<typeof createPaymentResponseSchema>

export const paymentLinkStatusResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    id: z.number(),
    status: paymentStatusSchema,
    provider: paymentProviderSchema,
    amount_kopecks: z.number(),
    payment_url: z.string().nullable().optional(),
    paid_at: z.string().nullable().optional(),
  }),
})
export type PaymentLinkStatusResponse = z.infer<typeof paymentLinkStatusResponseSchema>

export const staffPaymentLinksListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(paymentLinkSchema),
})
export type StaffPaymentLinksListResponse = z.infer<typeof staffPaymentLinksListResponseSchema>

export const createAdhocPaymentLinkInputSchema = z.object({
  provider: paymentProviderSchema,
  amount_kopecks: z.number().positive(),
  description: z.string().min(1).max(128),
  yandex_split: z.boolean().optional(),
  patient_phone: z.string().nullable().optional(),
  patient_label: z.string().nullable().optional(),
})
export type CreateAdhocPaymentLinkInput = z.infer<typeof createAdhocPaymentLinkInputSchema>
